import moment from "moment";

import { AppointmentCreateData } from "@/Appointments/Models/Appointment/AppointmentCreateDataComposite";
import { OptimizedAppointmentResult } from "@/Appointments/Models/OptimizedAppointment/OptimizedAppointmentResult";
import { PredefinedAppointmentDay } from "@/Appointments/Models/Scheduling/PredefinedAppointmentDay";
import { PredefinedAppointmentGroup } from "@/Appointments/Models/Scheduling/PredefinedAppointmentGroup";
import { PredefinedAppointmentItem } from "@/Appointments/Models/Scheduling/PredefinedAppointmentItem";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { BaseAction } from "@/Common/Stores/Actions/BaseAction";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ServerEvent, ServerEventWithRequest } from "@/Common/Stores/Actions/ServerEvent";
import { BaseServerOperationHandler } from "@/Common/Stores/Handlers/Server/BaseServerOperationHandler";
import { ITimeslotPredefinedAppointment } from "@/Patient/Models/Appointment/ITimeslotPredefinedAppointment";
import { ITimeslotResponseComposite } from "@/Patient/Models/Appointment/ITimeslotResponseComposite";
import { PatientStore } from "@/Patient/Stores/PatientStore";

class PatientAppointmentResponseHandler extends BaseServerOperationHandler {
    constructor(action: BaseAction) {
        super(action);

        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Appointment_Search, this.AppointmentSearch);
        this.RegisterOperation(ServerOperations.Server.Portal_AppointmentSearch, this.PortalAppointmentsSearch);

        // Save
        this.RegisterOperation(ServerOperations.Server.Myle_Calendar_CreateAppointment, this.Myle_Calendar_CreateAppointment);
        this.RegisterOperation(ServerOperations.Server.Portal_AppointmentSave, this.PortalAppointmentSave);
    }

    private AppointmentSearch(response: ServerEvent, store: PatientStore): void {
        sessionStore.hideGlobalOverlay();

        let OldSearchData: ITimeslotResponseComposite[] = response.Data.TimeSlotList;
        let searchId: number = response.Data.SearchId;
        let uiModel = store.PatientAppointmentModels[response.PatientId];

        if (searchId < store.lastReceivedSearchId)
            return;
        else if (searchId > store.lastReceivedSearchId) {
            uiModel = uiModel.set("TimeSlot", [].toImmutable());
            store.lastReceivedSearchId = searchId;
        }
        else { // Is Merge
            // Return to Search if No Results
            if (uiModel.get("TimeSlot").count() == 0 && (OldSearchData && OldSearchData.length == 0)) {
                window.setTimeout(() => {
                    var op = new LocalEvent(LocalPatientOperations.Appointment.ResetClick);
                    op.AddItem("keep", ["employee"]);
                    LocalEventDispatcher.dispatch(op);
                }, 50);
            }
        }

        uiModel = uiModel.set("RawAppointmentData", Immutable.List(OldSearchData));

        let timeSlot = uiModel.get("TimeSlot").toArray();

        OldSearchData.map((n: ITimeslotResponseComposite) => {
            let predefinedAppointmentDay = null;
            let ExistingDays = timeSlot.filter(b =>
                b.ControlDate.getFullYear() === moment(n.DayOfAppointment).toDate().getFullYear()
                && b.ControlDate.getMonth() === moment(n.DayOfAppointment).toDate().getMonth()
                && b.ControlDate.getDate() === moment(n.DayOfAppointment).toDate().getDate()
            );

            if (ExistingDays.length > 0)
                predefinedAppointmentDay = ExistingDays[0];
            else {
                let predefinedAppointmentDayStart = moment(n.DayOfAppointment).toDate();
                let isAfterLastDisplayedDay = predefinedAppointmentDayStart > timeSlot.map(x => x.ControlDate).sortByPropertyDesc(x => x).firstOrDefault();
                if (!isAfterLastDisplayedDay || timeSlot.length < 3) {
                    predefinedAppointmentDay = new PredefinedAppointmentDay();
                    predefinedAppointmentDay.ControlDate = predefinedAppointmentDayStart;

                    timeSlot.push(predefinedAppointmentDay);
                }
                else
                    return null;
            }

            n.AllPredefinedForOneSelection.map((s: ITimeslotPredefinedAppointment[]) => {
                var blueRectangle: PredefinedAppointmentGroup = new PredefinedAppointmentGroup();
                blueRectangle.MaxStart = s[0].Start;
                blueRectangle.MaxEnd = s[s.length - 1].End_From_Duration;

                for (let slot of s) {
                    var key = MyleHelper.GetStandardizedDateString(moment(slot.Start).toDate()) + MyleHelper.GetStandardizedDateString(moment(slot.End_From_Duration).toDate());
                    if (Object.keys(blueRectangle.PredefinedAppointments).indexOf(key) === -1) {
                        blueRectangle.PredefinedAppointments[key] = [];
                    }

                    var predefinedAppointment: PredefinedAppointmentItem = new PredefinedAppointmentItem();

                    let serviceDefinition = cacheStore.GetServiceDefinitionById(slot.ServiceDefinitionId);

                    predefinedAppointment.PredefinedAppointmentId = slot.PredefinedAppointmentId;
                    predefinedAppointment.SiteId = slot.SiteId;
                    predefinedAppointment.EmployeeId = slot.EmployeeId;
                    predefinedAppointment.Name = s != null && s.length > 0 ? slot.Name : cacheStore.GetString("AnyLabel");
                    predefinedAppointment.DefaultName = predefinedAppointment.Name;
                    predefinedAppointment.ServiceDefinitionId = slot.ServiceDefinitionId;
                    predefinedAppointment.DefaultServiceDefinitionId = predefinedAppointment.ServiceDefinitionId;
                    predefinedAppointment.IsNotifyPatientDisabled = !!serviceDefinition?.Model?.IsNotifyPatientDisabledByDefault;
                    predefinedAppointment.Subject = "";
                    predefinedAppointment.IsFromRuleSetAvailability = slot.IsFromRuleSetAvailability;

                    predefinedAppointment.Start = MyleHelper.GetStandardizedDateString(moment(slot.Start).toDate());
                    predefinedAppointment.Duration = slot.Duration;
                    predefinedAppointment.DefaultDuration = predefinedAppointment.Duration;
                    predefinedAppointment.SearchResult = slot.SearchResult;
                    predefinedAppointment.IsInEditMode = false;

                    predefinedAppointment.IsFromPortalSearch = false;

                    predefinedAppointment.HasQuestionnaire = !!serviceDefinition?.GetQuestionnaireId();

                    blueRectangle.PredefinedAppointments[key].push(predefinedAppointment);
                }
                predefinedAppointmentDay.BlueRectanlge.push(blueRectangle);
            });
        }).filter(x => x != null);

        uiModel = uiModel.set("TimeSlot", timeSlot.sortByProperty(b => b.ControlDate).toImmutable());
        store.PatientAppointmentModels[response.PatientId] = uiModel;
    }

    private PortalAppointmentsSearch(response: ServerEvent, store: PatientStore): void {
        sessionStore.hideGlobalOverlay();

        let optimizedResultData: OptimizedAppointmentResult[] = response.Data.OptimizedAppointmentResults;

        let searchId: number = response.Data.SearchId;
        let uiModel = store.PatientAppointmentModels[response.PatientId];

        // Ignore Old Results
        if (searchId < store.lastReceivedSearchId)
            return;
        else if (searchId > store.lastReceivedSearchId) {
            uiModel = uiModel.set("TimeSlot", [].toImmutable());
            store.lastReceivedSearchId = searchId;
        }
        else { // Is Merge
            // Return to Search if No Results
            if (uiModel.get("TimeSlot").count() == 0 && (optimizedResultData == null || optimizedResultData.length == 0)) {
                window.setTimeout(() => {
                    var op = new LocalEvent(LocalPatientOperations.Appointment.ResetClick);
                    op.AddItem("keep", ["employee"]);
                    LocalEventDispatcher.dispatch(op);
                }, 50);
            }
        }

        // Bind result
        var timeSlot = uiModel.get("TimeSlot").toArray();
        optimizedResultData.map(n => {
            let predefinedAppointmentDay = null;
            let ExistingDays = timeSlot.filter(b => b.ControlDate.getFullYear() === moment(n.StartDate).toDate().getFullYear() && b.ControlDate.getMonth() === moment(n.StartDate).toDate().getMonth() && b.ControlDate.getDate() === moment(n.StartDate).toDate().getDate());

            if (ExistingDays.length > 0)
                predefinedAppointmentDay = ExistingDays[0];
            else {
                let predefinedAppointmentDayStart = moment(n.StartDate).toDate();
                let isAfterLastDisplayedDay = predefinedAppointmentDayStart > timeSlot.map(x => x.ControlDate).sortByPropertyDesc(x => x).firstOrDefault();
                if (!isAfterLastDisplayedDay || timeSlot.length < 3) {
                    predefinedAppointmentDay = new PredefinedAppointmentDay();
                    predefinedAppointmentDay.ControlDate = predefinedAppointmentDayStart;

                    timeSlot.push(predefinedAppointmentDay);
                }
                else
                    return null;
            }

            var blueRectangle: PredefinedAppointmentGroup = new PredefinedAppointmentGroup();
            blueRectangle.MaxStart = n.StartDate;
            blueRectangle.MaxEnd = n.EndDate;
            blueRectangle.IsOptimizedSearch = true;
            blueRectangle.OptmizedResult = n;

            predefinedAppointmentDay.BlueRectanlge.push(blueRectangle);
        }).filter(x => x != null);

        uiModel = uiModel.set("TimeSlot", timeSlot.sortByProperty(b => b.ControlDate).toImmutable());
        store.PatientAppointmentModels[response.PatientId] = uiModel;
    }

    private Myle_Calendar_CreateAppointment(response: ServerEventWithRequest<{ Appointments: AppointmentCreateData[] }>, store: PatientStore): void {
        // Has Double Booking Error
        if (response.Errors.some(p => p.Code == "EmployeeDoubleBookError"))
            this.PopDoubleBookingWarning();
        else if (response.Data.Success == true) {
            let appointments = response.requestDto.Appointments;
            for (let appointment of appointments.filter(p => p.IsPatientNotificationsEnabled)) {
                patientStore.ActionsProfile.updatePatientProfileNotifications(appointment.PatientContactId, appointment.ContactPointNotificationList);
            }
            this.ReloadAppointmentSearch(response, store);
        }
        else
            sessionStore.showPopup(response.Errors);

        // refresh the appointmentlist of patient
        const appointmentModel = store.PatientHistoryAppointmentsModels[response.Data.PatientContactId];

        if (appointmentModel?.get("AppointmentListLoaded")) {
            store.ActionsHistory.LoadPatientAppointment(response.Data.PatientContactId, false, null, null, null, "");
        }
    }

    private PortalAppointmentSave(response: ServerEvent, store: PatientStore): void {
        // Has Double Booking Error
        if (response.Errors.some(p => p.Code == "EmployeeDoubleBookError"))
            this.PopDoubleBookingWarning();
        else if (response.Data.IsSuccess == true)
            this.ReloadAppointmentSearch(response, store);
        else
            sessionStore.showPopup(response.Errors);
    }

    private PopDoubleBookingWarning(): void {
        sessionStore.showPopup({
            content: [cacheStore.GetString("MessageDoubleBookingLabel"), cacheStore.GetString("MessageDoubleBooking2Label")],

            okLabel: cacheStore.GetString("YesLabel"),
            okAction: () => {
                let op = new LocalEvent(LocalOperations.Misc.ExternallyCalled_SavePatientAppointmentSearch);
                op.AddItem("force", true);
                MiscDispatcher.dispatch(op);
            },

            altButtonLabel: cacheStore.GetString("CancelLabel"),
            altButtonAction: () => {
                let op = new LocalEvent(LocalPatientOperations.Appointment.CancelSavePredefinedAppointment);
                LocalEventDispatcher.dispatch(op);
            }
        });
    }

    private ReloadAppointmentSearch(response: ServerEvent, store: PatientStore): void {
        var uiModel = store.PatientAppointmentModels[response.PatientId];

        uiModel = uiModel.set("ReferalPhoneNumber", "");
        uiModel = uiModel.set("SelectedReferalDoctor", Immutable.List([]));
        uiModel = uiModel.set("Notes", "");
        uiModel = uiModel.set("ReferalDoctorSearchString", "");
        uiModel = uiModel.set("ShowManagement", true);
        uiModel = uiModel.set("ShowSavePredefinedAppointment", false);
        uiModel = uiModel.set("SelectedOptimizedResult", null);

        store.PatientAppointmentModels[response.PatientId] = uiModel;
        sessionStore.hideGlobalOverlay();

        var sendDate = new Date(uiModel.get("TimeSlot").toArray()[0].ControlDate.getFullYear(), uiModel.get("TimeSlot").toArray()[0].ControlDate.getMonth(), uiModel.get("TimeSlot").toArray()[0].ControlDate.getDate(), 0, 0, 0);
        store.ActionsAppointment.LoadPredefineAppointmentAvailability(uiModel, MyleHelper.GetStandardizedDateString(sendDate), response.PatientId);
    }
}

export { PatientAppointmentResponseHandler };
